/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, Button, Card, List, Modal, Form, Input, InputNumber, Space, Spin } from 'antd';
import { commonStyles } from '../../styles/commonStyles';
import { TableOutlined } from '@ant-design/icons';
import MainLayout from '../../components/common/MainLayout';
import { useGetFieldByIdQuery, useUpdateFieldMutation, useDeleteFieldMutation, useCreatePlotMutation, useDeletePlotMutation } from '../../api/fieldsApi';
import { toastError } from '../../utils/toast';
import { css } from '@emotion/react';
import { useSave } from '../../contexts/WorldContext';

const styles = {
  actionButtons: css({ marginBottom: 16 }),
  plotsList: css({ marginTop: 16 }),
};

const FieldPage: React.FC = () => {
  const { saveId, fieldId } = useParams<{ saveId: string; fieldId: string }>();
  const { t } = useTranslation('planning');
  const save = useSave();
  const farmId = save.farmId;
  const navigate = useNavigate();
  const { data: field, isLoading, isError, refetch } = useGetFieldByIdQuery(
    { saveId: saveId ?? '', fieldId: fieldId || '' },
    { skip: !saveId || !fieldId, refetchOnMountOrArgChange: true }
  );
  const [updateField, { isLoading: updating }] = useUpdateFieldMutation();
  const [deleteField] = useDeleteFieldMutation();
  const [createPlot, { isLoading: creatingPlot }] = useCreatePlotMutation();
  const [deletePlot] = useDeletePlotMutation();
  const [editOpen, setEditOpen] = useState(false);
  const [plotOpen, setPlotOpen] = useState(false);
  const [form] = Form.useForm();
  const [plotForm] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();
  const actualFarmId = farmId || field?.farmId?.toString();
  const farmName = save.farmName || t('fieldPage.save');

  const handleOpenEdit = () => {
    if (!field) return;
    form.setFieldsValue({
      name: field.name,
      area: field.area ?? undefined,
      location_coords: field.location_coords ?? undefined,
      description: field.description ?? undefined,
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!field) return;
    const values = await form.validateFields();
    await updateField({ saveId: saveId || '', id: String(field.id), payload: values }).unwrap();
    setEditOpen(false);
    refetch();
  };

  const handleDelete = () => {
    if (!field) return;
    modal.confirm({
      title: t('fieldPage.deleteFieldConfirm'),
      okText: t('common:actions.delete'),
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        try {
          await deleteField({ saveId: saveId || '', id: String(field.id) }).unwrap();
          navigate(actualFarmId ? `/saves/${saveId}` : '/');
        } catch (e) {
          toastError(e);
        }
      },
    });
  };

  const handleCreatePlot = async () => {
    if (!field) return;
    const values = await plotForm.validateFields();
    await createPlot({ saveId: saveId || '', fieldId: String(field.id), ...values }).unwrap();
    setPlotOpen(false);
    plotForm.resetFields();
    refetch();
  };

  const actionHandlers: Record<string, () => void> = {
    'fields.update': handleOpenEdit,
    'fields.delete': handleDelete,
    'plots.create': () => setPlotOpen(true),
  };

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/saves">{t('fieldPage.saves')}</Link> },
            { title: actualFarmId ? <Link to={`/saves/${saveId}`}>{farmName}</Link> : farmName },
            { title: field?.name || fieldId },
          ]}
        />
      }
    >
      {contextHolder}
      {isLoading && <Spin />}
      {isError && <div>{t('fieldPage.loadFailed')}</div>}
      {field && (
        <>
          <Space css={styles.actionButtons}>
            {Array.isArray(field.actions) && field.actions.length > 0 && field.actions.map((a: any) => (
              <Button
                key={a.code}
                type={a.code === 'fields.update' ? 'primary' : 'default'}
                danger={a.state === 'danger'}
                onClick={() => actionHandlers[a.code]?.()}
              >
                {a.title}
              </Button>
            ))}
            <Button
              icon={<TableOutlined />}
              onClick={() => navigate(`/saves/${saveId}/fields/${fieldId}/plan`)}
            >
              {t('fieldPage.editPlan')}
            </Button>
          </Space>
          <h2>{t('fieldPage.fieldTitle', { name: field.name })}</h2>
          <p>{t('fieldPage.areaHa', { area: field.area ?? '-' })}</p>
          <p>{t('fieldPage.coordsValue', { coords: field.location_coords || '-' })}</p>
          <p>{t('fieldPage.descriptionValue', { description: field.description || '-' })}</p>

          {Array.isArray((field as any).plots) && (field as any).plots.length > 0 && (
            <div css={styles.plotsList}>
              <h3>{t('fieldPage.plots')}</h3>
              <List
                grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3 }}
                dataSource={(field as any).plots}
                renderItem={(plot: any) => (
                  <List.Item key={plot.id}>
                    <Card
                      title={plot.name}
                      hoverable
                      onClick={() => navigate(`/saves/${saveId}/plots/${plot.id}/plan`)}
                      actions={(() => {
                        const items: any[] = [];
                        if (Array.isArray(plot.actions)) {
                          plot.actions.forEach((a: any) => {
                            if (a.code === 'plots.delete') {
                              items.push(
                                <Button
                                  key="delete"
                                  danger
                                  type="link"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    modal.confirm({
                                      title: t('fieldPage.deletePlotConfirm'),
                                      okText: t('common:actions.delete'),
                                      cancelText: t('common:actions.cancel'),
                                      onOk: async () => {
                                        await deletePlot({ saveId: saveId || '', plotId: String(plot.id), fieldId: String(field.id) }).unwrap();
                                        refetch();
                                      },
                                    });
                                  }}
                                >
                                  {a.title || t('fieldPage.deletePlot')}
                                </Button>,
                              );
                            }
                          });
                        }
                        return items.length ? items : undefined;
                      })()}
                    >
                      {'area' in plot && <p>{t('fieldPage.areaHa', { area: plot.area ?? '-' })}</p>}
                      {'location_coords' in plot && <p>{t('fieldPage.coordsValue', { coords: plot.location_coords || '-' })}</p>}
                      {'description' in plot && <p>{t('fieldPage.descriptionValue', { description: plot.description || '-' })}</p>}
                    </Card>
                  </List.Item>
                )}
              />
            </div>
          )}
        </>
      )}
      <Modal
        title={t('fieldPage.editField')}
        open={editOpen}
        onOk={handleSaveEdit}
        onCancel={() => setEditOpen(false)}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={updating}
      >
        <Form form={form} layout="vertical">
          <Form.Item label={t('fieldPage.form.name')} name="name" rules={[{ required: true, message: t('fieldPage.form.nameRequired') }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('fieldPage.form.areaHa')} name="area">
            <InputNumber css={commonStyles.fullWidth} />
          </Form.Item>
          <Form.Item label={t('fieldPage.form.coords')} name="location_coords">
            <Input />
          </Form.Item>
          <Form.Item label={t('common:labels.description')} name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('fieldPage.createPlot')}
        open={plotOpen}
        onOk={handleCreatePlot}
        onCancel={() => setPlotOpen(false)}
        okText={t('common:actions.create')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={creatingPlot}
      >
        <Form form={plotForm} layout="vertical">
          <Form.Item label={t('fieldPage.form.name')} name="name" rules={[{ required: true, message: t('fieldPage.form.nameRequired') }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('fieldPage.form.areaHa')} name="area">
            <InputNumber css={commonStyles.fullWidth} />
          </Form.Item>
          <Form.Item label={t('fieldPage.form.coords')} name="location_coords">
            <Input />
          </Form.Item>
          <Form.Item label={t('common:labels.description')} name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default FieldPage;
